import React, { useState, useEffect } from 'react';
import { Image, Video, File, Copy, Check } from 'lucide-react';
import { ProcessedFileData } from '../../../types/fileProcessing';
import { SecureImage } from './SecureImage';


interface FileCardBaseProps {
  file: ProcessedFileData;
  children: React.ReactNode;
  className?: string;
}

export const FileCardBase: React.FC<FileCardBaseProps> = ({ children, className = '' }) => {
  return (
    <div className={`border border-gray-200 rounded-lg ${className}`}>
      {children}
    </div>
  );
};

interface FileThumbnailProps {
  file: ProcessedFileData;
  size?: 'small' | 'large';
  albumSlug?: string;
}

export const FileThumbnail: React.FC<FileThumbnailProps> = ({ file, size = 'small', albumSlug }) => {
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const getFileIcon = () => {
    const iconSize = size === 'large' ? 'w-12 h-12' : 'w-8 h-8';
    if (file.type.startsWith('image/')) {
      return <Image className={`${iconSize} text-blue-500`} />;
    } else if (file.type.startsWith('video/')) {
      return <Video className={`${iconSize} text-purple-500`} />;
    }
    return <File className={`${iconSize} text-gray-500`} />;
  };

  // Use full width with responsive height based on size
  const containerClasses = size === 'large'
    ? 'w-full max-h-64 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden relative'
    : 'w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0 relative';

  // MEMORY OPTIMIZATION: Only show thumbnails for complete files (from S3)
  // For queuing/processing files, just show filename without thumbnail to save memory
  if (file.status !== 'complete') {
    return (
      <div className={containerClasses}>
        {getFileIcon()}
      </div>
    );
  }

  // SIMPLE & ROBUST: Always show icon first, then try to load image
  const handleImageError = () => {
    // Thumbnail failed, showing icon instead
    setImageError(true);
    setIsLoading(false);
  };

  const handleImageLoad = () => {
    // Removed console.log for performance
    setImageError(false);
    setIsLoading(false);
  };

      // SECURE BLOB: For complete files, use SecureImage with blob URLs (secure and stable)
      if (file.assetId && file.type.startsWith('image/')) {
        return (
          <div className={containerClasses}>
            <SecureImage
              assetId={file.assetId}
              version="thumbnail"
              alt={file.name}
              className={`${size === 'large' ? 'w-full object-contain' : 'w-full h-full object-cover'} rounded-lg`}
              onError={handleImageError}
              onLoad={handleImageLoad}
              loading="lazy" // 🚀 LAZY LOAD: Only load when near viewport (Facebook-style)
            />
          </div>
        );
      }

  // Fallback: Always show icon
  return (
    <div className={containerClasses}>
      {getFileIcon()}
    </div>
  );
};

interface StatusBadgeProps {
  status: ProcessedFileData['status'];
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({ status }) => {
  const getStatusConfig = () => {
    switch (status) {
      case 'queuing':
        return { text: 'Queuing', className: 'bg-yellow-100 text-yellow-800' };
      case 'validating':
        return { text: 'Processing', className: 'bg-blue-100 text-blue-800' };
      case 'uploading':
        return { text: 'Processing', className: 'bg-blue-100 text-blue-800' };
      case 'processing':
        return { text: 'Processing', className: 'bg-blue-100 text-blue-800' };
      case 'complete':
        return { text: 'Complete', className: 'bg-green-100 text-green-800' };
      case 'failed':
        return { text: 'Failed', className: 'bg-red-100 text-red-800' };
      default:
        return { text: 'Unknown', className: 'bg-gray-100 text-gray-800' };
    }
  };

  const config = getStatusConfig();

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
      {config.text}
    </span>
  );
};

interface CopyButtonProps {
  content: string;
  onCopy?: (content: string) => void;
}

export const CopyButton: React.FC<CopyButtonProps> = ({ content, onCopy }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      onCopy?.(content);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  return (
    <button
      onClick={handleCopy}
      className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
      title="Copy to clipboard"
    >
      {copied ? (
        <Check className="w-4 h-4 text-green-500" />
      ) : (
        <Copy className="w-4 h-4" />
      )}
    </button>
  );
};